import { useLingui } from '@lingui/react/macro';
import { Check, Copy } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { useNow } from '@/shared/page';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';

import {
  amortizationDate,
  amortizationMoney,
  amortizationPercent,
  amortizationTruncatedPercent,
  indexerText,
  isEmptyResult,
  operationTerm,
  scheduleTotal,
  termText,
  type AmortizationResult,
} from './amortization';

/**
 * Os itens de "Resultados da operação", na ordem e com as chaves do legado (AmortizationQuery.tsx:233-729;
 * as mesmas do `removedFromPrintList` da impressão).
 */
export const AMORTIZATION_SUMMARY_FIELDS = [
  'startDate',
  'dueDate',
  'termInMonths',
  'indexer',
  'initialValue',
  'comm',
  'finTax',
  'liquidValue',
  'grossValue',
  'commTc',
  'commNonTc',
  'monthlyInterest',
  'yearlyInterest',
  'effectiveMonthlyCost',
  'effectiveYearlyCost',
  'paymentFixedCosts',
] as const;
export type AmortizationSummaryField = (typeof AMORTIZATION_SUMMARY_FIELDS)[number];

/**
 * Visíveis por padrão. Tarifa de Cadastro e Tarifas/Seguro só aparecem com `Hidden: false` no
 * `FORM_FIELDS` (AmortizationQuery.tsx:144-145); Tarifas por parcela só no `price` com o regime de
 * previdência e `product.paymentFixedCosts > 0` (:692-695). Quem decide é o módulo.
 */
const HIDDEN_BY_DEFAULT: ReadonlySet<AmortizationSummaryField> = new Set(['commTc', 'commNonTc', 'paymentFixedCosts']);

/** Texto, não número: fora do DM Mono (U39 §11 vale para dinheiro, taxa e data). */
const TEXT_FIELDS: ReadonlySet<AmortizationSummaryField> = new Set(['termInMonths', 'indexer']);

export interface AmortizationSummaryProps {
  readonly result: AmortizationResult | null | undefined;
  /** Liga ou desliga itens (o `Hidden` do `FORM_FIELDS` por tenant). */
  readonly fields?: Partial<Record<AmortizationSummaryField, boolean>>;
  /** Rótulo por tenant (`GetLabelForField('amortization.<campo>')`). */
  readonly labels?: Partial<Record<AmortizationSummaryField, string>>;
  /** "N meses" em vez de anos e meses (`FORM_FIELDS.numberOfPayments` padrão `Monthly`, AmortizationQuery.tsx:100-128). */
  readonly termInMonths?: boolean;
  /** Indexador do formulário, quando difere do resultado (o legado lê do formulário, :111-113). */
  readonly indexer?: Pick<AmortizationResult, 'indexer' | 'indexerValue'> | null | undefined;
  /** Mostra "Id da simulação" com copiar (o protótipo o põe no cabeçalho dos resultados). */
  readonly simulationId?: string | null | undefined;
  /** Aviso sem resultado; padrão: `Clique em "Simular" para visualizar a operação!` (:185-189). */
  readonly emptyMessage?: string;
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly presentation?: 'web' | 'mobile';
  readonly className?: string;
}

interface Item {
  readonly id: AmortizationSummaryField;
  readonly label: string;
  readonly value: string;
}

function useItems({ result, fields, labels, termInMonths, indexer }: AmortizationSummaryProps): readonly Item[] {
  const { i18n, t } = useLingui();
  const now = useNow();
  const locale = i18n.locale;
  const money = (value: unknown) => amortizationMoney(value, locale);
  const date = (value: unknown) => amortizationDate(value, locale) ?? t`N/D`;
  const monthly = (result?.paymentPeriodicity?.periodicity || result?.periodicity)?.toLowerCase() === 'monthly';
  const payments = result?.numberOfPayments;
  const term = termInMonths && monthly && payments ? t`${payments} meses` : termText(i18n, operationTerm(result, now));
  const values: Readonly<Record<AmortizationSummaryField, readonly [string, string]>> = {
    startDate: [t`Data de Emissão`, date(result?.startDate)],
    dueDate: [t`Data de Vencimento`, date(result?.dueDate)],
    termInMonths: [t`Prazo`, term],
    indexer: [t`Indexador`, indexerText(indexer ?? result) ?? t`Sem indexador pós-fixado`],
    initialValue: [t`Valor do Contrato`, money(result?.initialValue)],
    comm: [t`Custo da emissão`, money(result?.comm)],
    finTax: [t`IOF`, money(result?.finTax)],
    liquidValue: [t`Valor Líquido`, money(result?.liquidValue)],
    grossValue: [t`Valor futuro`, money(scheduleTotal(result?.paymentScheduleItems))],
    commTc: [t`Tarifa de Cadastro`, money(result?.commTc)],
    commNonTc: [t`Tarifas/Seguro`, money(result?.commNonTc)],
    monthlyInterest: [t`Taxa A.M`, amortizationTruncatedPercent(result?.monthlyInterest, locale)],
    yearlyInterest: [t`Taxa A.A`, amortizationPercent(result?.yearlyInterest, locale)],
    effectiveMonthlyCost: [t`CET A.M.`, amortizationTruncatedPercent(result?.effectiveMonthlyCost, locale)],
    effectiveYearlyCost: [t`CET A.A.`, amortizationTruncatedPercent(result?.effectiveYearlyCost, locale)],
    paymentFixedCosts: [t`Tarifas por parcela`, money(result?.paymentFixedCosts)],
  };
  return AMORTIZATION_SUMMARY_FIELDS.filter((id) => fields?.[id] ?? !HIDDEN_BY_DEFAULT.has(id)).map((id) => ({
    id,
    label: labels?.[id] ?? values[id][0],
    value: values[id][1],
  }));
}

function SimulationId({ id }: { readonly id: string }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  return (
    <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      <span>{t`Id da simulação`}</span>
      <span className="font-mono text-foreground">{id}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 text-muted-foreground"
        aria-label={copied ? t`Copiado` : t`Copiar o id da simulação`}
        onClick={() => {
          void navigator.clipboard.writeText(id).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? <Check aria-hidden className="size-3.5" /> : <Copy aria-hidden className="size-3.5" />}
      </Button>
      <span>{t`use para recuperar a simulação`}</span>
    </p>
  );
}

/**
 * "Resultados da operação" (AmortizationQuery.tsx): datas, prazo, indexador, valores, IOF, taxas e CET
 * de um resultado de amortização, com os formatadores do legado. Web em grade que cresce com o
 * container; iPhone em cartões de duas colunas (protótipo `m_operation`).
 */
export function AmortizationSummary(props: AmortizationSummaryProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const items = useItems(props);
  const mobile = (props.presentation ?? (isMobile ? 'mobile' : 'web')) === 'mobile';
  const { title, description, simulationId, className } = props;
  const empty = isEmptyResult(props.result);
  return (
    <div data-slot="amortization-summary" className={cn('@container/summary flex min-w-0 flex-col gap-3', className)}>
      {title || simulationId ? (
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
          {title ? (
            <div className="flex flex-col gap-0.5">
              <h3 className="text-sm font-semibold text-foreground">{title}</h3>
              {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
            </div>
          ) : null}
          {simulationId && !empty ? <SimulationId id={simulationId} /> : null}
        </div>
      ) : null}
      {empty ? (
        <Alert tone="warn">{props.emptyMessage ?? t`Clique em "Simular" para visualizar a operação!`}</Alert>
      ) : (
        <dl
          className={cn(
            'grid gap-2',
            mobile ? 'grid-cols-2' : 'grid-cols-1 gap-3 @min-[361px]/summary:grid-cols-3 @min-[721px]/summary:grid-cols-5',
          )}
        >
          {items.map((item) => (
            <div key={item.id} data-field={item.id} className={cn('flex min-w-0 flex-col gap-0.5 rounded-control border border-border bg-card', mobile ? 'px-3 py-2.5' : 'px-3.5 py-3')}>
              <dt className="text-xs text-muted-foreground">{item.label}</dt>
              <dd className={cn('font-semibold break-words text-foreground', TEXT_FIELDS.has(item.id) ? 'font-sans' : 'font-mono tabular-nums', mobile ? 'text-sm' : 'text-base')}>{item.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
